import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getI18n, useTranslation } from 'react-i18next';

import moment from 'moment';
import classnames from 'classnames';

import { useAppDispatch, useAppSelector } from 'app/store/store';
import { useVesselCharacteristics, useGetDestinationInfo, useGetWeatherData } from 'app/hooks';

import { Table, Button, Pagination, Typo, DoubleValue, InfoWrapper, VesselIcon, Icon, VesselPhoto } from 'app/components';
import { CountryFlag } from 'CountryFlag/CountryFlag';

import { columns } from './columns';

import { utils } from 'app/utils';

import { setFindAton, setFindVessel } from 'app/utils/selectedTargetsSlice';

import { throttle, getDateRange, getDiffInMinutes, glToStringHDMS } from 'app/helpers';

import { PortTargetEventsRequestI, PortTargetEventTypeEnum, SortingInterface } from '../PortDetails/PortEvents';

import { useGetShipInfoQuery } from 'app/api/ships';
import { useAddToFleetMutation } from 'app/api/fleet';
import { useGetVesselEventsDataMutation } from 'app/api/facade';
import { useGetAverageSpeedQuery, useGetOnTheMoveQuery, useGetTravelledDistanceQuery } from 'app/api/target';
import { TabPanel, Tabs } from 'app/components/Tab';
import { SimilarShipsTable } from './SimilarShipsTable';
import { useFormatMoment } from 'app/hooks/useFormatMoment';
import { AnchorageEventsTable } from './AnchorageEventsTable';

import styles from './VesselDetails.module.scss';

const initialSort: SortingInterface = {
  direction: 'DESC',
  by: 'EVENT_DATE_TIME',
};

interface VesselDetailsI {
  setInitCenter: React.Dispatch<React.SetStateAction<number[]>>;
}

export const VesselDetails = ({ setInitCenter }: VesselDetailsI) => {
  const { t } = useTranslation();

  const formatMoment = useFormatMoment();
  const { mmsi } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const user = useAppSelector((state) => state.userSlice.user);
  const userConfig = useAppSelector((state) => state.userSlice.uiConfig);
  const language = useMemo(() => getI18n().language, [t]);
  const itemsPerPage = 20;

  const { data: vessel, ...vesselRequest } = useGetShipInfoQuery(Number(mmsi));
  const { windDirection, windSpeed, airTemp } = useGetWeatherData(vessel?.result?.position) || {};
  const [addToFleet] = useAddToFleetMutation();

  const tabs = [
    {
      value: 'vessel-calls',
      label: t('PortEvents'),
    },
    {
      value: 'anchorage',
      label: t('Anchorages'),
    },
    {
      value: 'similar-vessels',
      label: t('SimilarShips'),
    },
  ];

  const [tabValue, setTabValue] = useState<string>();

  const handleChange = (event: React.SyntheticEvent, newValue: any) => {
    setTabValue(newValue);
  };

  const {
    widthDimension,
    lengthDimension,
    withDraught,
    withDepth,
    withMaximumDraft,
    withFuelBunker,
    withDisplacement,
    withDeadWeight,
    withGrossTonnage,
  } = useVesselCharacteristics(vessel?.result);

  const [getVesselEventsDataMutation, { data: vesselEvents, ...vesselEventsRequest }] = useGetVesselEventsDataMutation();

  const debounceGetTargetsHandler = useCallback(throttle(getVesselEventsDataMutation, 500), []);

  const [sorting, setSorting] = useState<SortingInterface>(initialSort);

  const [currentPage, setCurrentPage] = useState(1);

  const { eta, destination, calculatedETA, departurePortName, departurePortKey, departureTime } = useGetDestinationInfo(vessel);

  const diffInMinutes = getDiffInMinutes(calculatedETA, eta);

  const isDataDeviationExcessiveClass = diffInMinutes !== undefined && diffInMinutes >= 30 ? styles.date_deviation_excessive : '';

  const prediction = vessel?.result?.destinationPorts;

  const name = utils.getShipName(vessel?.result);

  const profile = useAppSelector((state) => state.userSlice.profile);
  const coordinatesFormat = useMemo(() => profile?.ui?.['coordinatesFormat'] ?? 'DMS', [profile]);

  const isAton: boolean = useMemo(() => {
    if (vessel?.result?.registryData?.specialType && vessel.result.registryData?.specialType === 'ATON') return true;
    return false;
  }, [vessel?.result?.registryData?.specialType]);

  const position: string | undefined = useMemo(() => {
    if (typeof vessel?.result?.position?.latitude !== 'number' || typeof vessel?.result?.position?.longitude !== 'number') {
      return undefined;
    }
    return glToStringHDMS([vessel.result.position.longitude, vessel.result.position.latitude], coordinatesFormat);
  }, [vessel?.result?.position, coordinatesFormat]);

  const inmarsat =
    vessel?.result?.registryData?.inmarsatNumbers && vessel.result.registryData.inmarsatNumbers.length > 0
      ? vessel.result.registryData.inmarsatNumbers.join(';')
      : null;

  const lastUpload: string | undefined = useMemo(
    () => (vessel?.result?.t !== undefined ? moment(vessel.result.t).format('DD.MM.YYYY \u2013 HH:mm:ss') : undefined),
    [vessel?.result?.t],
  );

  const lastMovementDateTime = vessel?.result?.lastMovementDateTime
    ? formatMoment(moment(vessel.result.lastMovementDateTime))
    : null;
  const firstPositionDateTime = vessel?.result?.firstPositionDateTime
    ? formatMoment(moment(vessel.result.firstPositionDateTime))
    : null;

  const { today, prevDay, fromDate, new_today, new_prevDay, new_fromDate } = useMemo(
    () => getDateRange(1, userConfig && userConfig?.callEventMaxDepth ? userConfig?.callEventMaxDepth : 'P720TH'),
    [userConfig],
  );

  const tableData = useMemo(
    () => vesselEvents?.results?.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
    [vesselEvents?.results, currentPage, itemsPerPage],
  );

  const body: PortTargetEventsRequestI = useMemo(
    () => ({
      filter: {
        interval: {
          from: fromDate,
          to: today,
        },
        datetime: `${new_fromDate} / ${new_today}`,
        type: [PortTargetEventTypeEnum.LowerTheAnchor, PortTargetEventTypeEnum.RaiseTheAnchor],
      },
      order: sorting.direction === 'default' ? initialSort : sorting,
    }),
    [sorting],
  );

  const { data: travelledDistanceForDay } = useGetTravelledDistanceQuery({
    mmsi: Number(mmsi),
    from: prevDay,
    to: today,
    datetime: `${new_prevDay} / ${new_today}`,
  });

  const { data: travelledDistanceForMonth } = useGetTravelledDistanceQuery({
    mmsi: Number(mmsi),
    from: fromDate,
    to: today,
    datetime: `${new_fromDate} / ${new_today}`,
  });

  const { data: averageSpeedTotal } = useGetAverageSpeedQuery({
    mmsi: Number(mmsi),
    from: fromDate,
    to: today,
    datetime: `${new_fromDate} / ${new_today}`,
    mode: 'TOTAL',
  });

  const { data: averageSpeedOnTheMove } = useGetAverageSpeedQuery({
    mmsi: Number(mmsi),
    from: fromDate,
    to: today,
    datetime: `${new_fromDate} / ${new_today}`,
    mode: 'ON_THE_MOVE',
  });

  const { data: onTheMove } = useGetOnTheMoveQuery({
    mmsi: Number(mmsi),
    from: fromDate,
    to: today,
    datetime: `${new_fromDate} / ${new_today}`,
  });

  const handleAddToFleet = () => {
    const user_id = user?.value.sub;

    if (user_id && typeof vessel?.result?.mmsi === 'number') {
      addToFleet({ mmsiArray: [vessel.result.mmsi] })
        .unwrap()
        .then(() => {
          vesselRequest.refetch();
        });
    }
  };

  const showVessel = useCallback(() => {
    if (isAton) {
      dispatch(setFindAton(vessel?.result));
    } else {
      dispatch(setFindVessel(vessel?.result));
    }

    navigate('/mapview');
  }, [vessel]);

  const handleGoBack = () => {
    const local_center = localStorage.getItem('center');
    const parsed_local_center = local_center !== null ? JSON.parse(local_center) : [0, 0];
    setInitCenter([parsed_local_center[0], parsed_local_center[1]]);
    navigate(-1);
  };

  useEffect(() => {
    if (!tabValue) {
      setTabValue(tabs[0]?.value);
    }
  }, [tabs]);

  useEffect(() => {
    if (language) {
      vesselRequest.refetch();
    }
  }, [language]);

  useEffect(() => {
    if (user && mmsi) {
      setCurrentPage(1);
      debounceGetTargetsHandler({ body, mmsi });
    }
  }, [sorting, mmsi]);

  useEffect(() => {
    if (user && mmsi) {
      debounceGetTargetsHandler({ body, mmsi });
    }
  }, [user, language]);

  return (
    <div className={styles.page}>
      {vessel?.result && (
        <>
          <div className={styles.heading_container}>
            <Button iconName="back" onClick={handleGoBack} disabled={history.state.idx === 0} variant="icon-only" iconSize="13" />
            <VesselIcon vessel={vessel?.result} />
            <h2 className={styles.heading}>{name}</h2>
            {position && (
              <div onClick={showVessel} className={styles.icon_target}>
                <Icon name="aim" size="14px" />
              </div>
            )}
          </div>
          <div className={styles.content}>
            <div className={styles.sub_header}>
              <div className={styles.type}>
                {vessel.result?.meta?.type ? vessel.result.meta.type : <div className={styles.no_data}>&mdash;</div>}
              </div>
              {vessel.result?.belongsToFleet ? (
                <Link className={styles.fleet} to="/fleet">
                  <Icon name="my-fleet" />
                  {t('FleetLabel')}
                </Link>
              ) : (
                <Button variant="ghost" onClick={handleAddToFleet}>
                  <Icon name="add" size="12px" />
                  {t('FleetLabel')}
                </Button>
              )}
            </div>
            <div className={styles.columned_data_block}>
              <div className={styles.column}>
                <InfoWrapper label={`${t('Destination')}:`} value={destination} strong />
                {prediction && prediction.length !== 0 && (
                  <InfoWrapper
                    label={`${t('PredictedPort')}:`}
                    value={
                      <div>
                        {prediction.map((port: any, index: number) => (
                          <span key={port.key}>
                            <Link className={styles.port_link} to={`/ports/${port.key}`}>
                              {port.name}
                            </Link>
                            {index !== (prediction.length ?? 0) - 1 && ', '}
                          </span>
                        ))}
                      </div>
                    }
                  />
                )}
                {departurePortKey && (
                  <InfoWrapper
                    label={`${t('DeparturePort')}:`}
                    value={
                      <>
                        <Link
                          key={departurePortName || departurePortKey}
                          className={styles.port_link}
                          to={`/ports/${departurePortKey}`}
                        >
                          {departurePortName || departurePortKey}
                        </Link>
                      </>
                    }
                  />
                )}
              </div>
              <div className={styles.column}>
                <InfoWrapper label={`${t('ETA')}:`} value={eta} strong />
                {typeof calculatedETA === 'string' && (
                  <InfoWrapper
                    strong
                    label={`${t('CalculatedETA')}:`}
                    value={<strong className={isDataDeviationExcessiveClass}>{calculatedETA}</strong>}
                  />
                )}
                {departureTime && <InfoWrapper label={`${t('ATD')}:`} value={departureTime} />}
              </div>
            </div>
            <div className={styles.columned_data_block}>
              <div className={styles.column}>
                <InfoWrapper
                  label={`${t('Flag')}:`}
                  value={
                    typeof vessel.result?.meta?.countryName !== 'undefined' ? (
                      <>
                        {vessel.result?.meta?.countryCode && (
                          <CountryFlag countryCode={vessel.result.meta.countryCode.toLowerCase()} />
                        )}
                        <strong>
                          {vessel.result?.meta?.countryName} ({vessel.result?.meta?.countryCode})
                        </strong>
                      </>
                    ) : (
                      <div className={styles.no_data}>&mdash;</div>
                    )
                  }
                />
                <InfoWrapper label={`${t('MMSI')}:`} value={vessel.result?.mmsi} strong />
                <InfoWrapper label={`${t('IMO')}:`} value={vessel.result?.registryData?.imo ?? vessel.result?.staticData?.imo} />
                <InfoWrapper
                  label={`${t('CallSign')}:`}
                  value={vessel.result?.registryData?.callSign ?? vessel.result?.staticData?.callSign}
                />
                <InfoWrapper label={`${t('TransponderClass')}:`} value={vessel.result?.registryData?.transponderClass} />
                <>
                  <InfoWrapper label={`${t('LengthDimension')}:`} value={lengthDimension} />
                  <InfoWrapper label={`${t('WidthDimension')}:`} value={widthDimension} />
                  <InfoWrapper label={`${t('MaximumDraft')}:`} value={withMaximumDraft} />
                  <InfoWrapper label={`${t('Draught')}:`} value={withDraught} />
                </>
              </div>

              <div className={classnames(styles.column, styles.column_photo)}>
                <VesselPhoto imo={vessel.result?.registryData?.imo ?? vessel.result?.staticData?.imo} />
              </div>
            </div>
            <div className={styles.position_data}>
              <div className={styles.column}>
                <InfoWrapper label={`${t('NavStatus')}:`} value={vessel.result?.meta?.status} strong />
                <InfoWrapper
                  clickable={!!position}
                  label={`${t('Position')}:`}
                  value={position}
                  valueIcon={<Icon name="aim" size="14px" color="#464958" />}
                  onClick={showVessel}
                />
              </div>
              <div className={styles.column}>
                <InfoWrapper label={`${t('DataSourceLabel')}:`} value={vessel.result?.meta?.source} />
                <InfoWrapper
                  label={`${t('DataObtained')}:`}
                  title={
                    typeof vessel?.result?.sourceCode === 'number' ? `${t('Source')}: ${vessel.result.sourceCode}` : undefined
                  }
                  value={lastUpload}
                />
              </div>
            </div>
            <div className={styles.statistics}>
              <DoubleValue
                label={`${t('TravelledForDay')}:`}
                value={travelledDistanceForDay?.nmi ? `${travelledDistanceForDay.nmi.toFixed(1)} NM` : undefined}
                subValue={travelledDistanceForDay?.km ? `${travelledDistanceForDay.km.toFixed(1)} ${t('Km')}` : undefined}
              />
              <DoubleValue
                label={`${t('TravelledForMonth')}:`}
                value={travelledDistanceForMonth?.nmi ? `${travelledDistanceForMonth.nmi.toFixed(1)} NM` : undefined}
                subValue={travelledDistanceForMonth?.km ? `${travelledDistanceForMonth.km.toFixed(1)} ${t('Km')}` : undefined}
              />
              <DoubleValue
                label={`${t('AverageSpeedForMonth')}:`}
                value={averageSpeedTotal?.kt ? `${averageSpeedTotal.kt.toFixed(2)} ${t('VesselSpeedUnit')}` : undefined}
                subValue={averageSpeedTotal?.kmh ? `${averageSpeedTotal.kmh.toFixed(2)}  ${t('KmH')}` : undefined}
                title={t('HoverTextAverageSpeedTotal')}
              />
              <DoubleValue
                label={`${t('AverageSpeedOnTheMoveForMonth')}:`}
                value={averageSpeedOnTheMove?.kt ? `${averageSpeedOnTheMove.kt.toFixed(2)} ${t('VesselSpeedUnit')}` : undefined}
                subValue={averageSpeedOnTheMove?.kmh ? `${averageSpeedOnTheMove.kmh.toFixed(2)}  ${t('KmH')}` : undefined}
                title={t('HoverTextAverageSpeedOnTheMove')}
              />
              <DoubleValue
                label={`${t('DaysOnTheMove')}:`}
                value={onTheMove?.date ? `${onTheMove.date.length}` : undefined}
                subValue={t('DaysOnTheMoveSubValue')}
              />
            </div>
            <div className={styles.columned_data_block}>
              <div className={styles.column}>
                <InfoWrapper label={`${t('AirTemperature')}:`} value={airTemp} />
              </div>
              <div className={styles.column}>
                <InfoWrapper
                  label={`${t('Wind')}:`}
                  value={windSpeed && windDirection ? `${windSpeed},\u0020${windDirection}` : undefined}
                />
              </div>
            </div>
            <div className={styles.columned_data_block}>
              <div className={styles.column}>
                <InfoWrapper label={`${t('Depth')}:`} value={withDepth} />
                <InfoWrapper label={`${t('IceClass')}:`} value={vessel.result?.registryData?.iceClass} />
                <InfoWrapper label={`${t('SimbolOfClass')}:`} value={vessel.result?.registryData?.soc} />
              </div>
              <div className={styles.column}>
                <InfoWrapper label={`${t('Displacement')}:`} value={withDisplacement} />
                <InfoWrapper label={`${t('DeadWeight')}:`} value={withDeadWeight} />
                <InfoWrapper label={`${t('GrossTonnage')}:`} value={withGrossTonnage} />
              </div>
            </div>
            <div className={styles.columned_registry_data_block}>
              <div className={styles.column}>
                <InfoWrapper label={`${t('Owner')}:`} value={vessel.result?.registryData?.owner} />
                <InfoWrapper label={`${t('HomePort')}:`} value={vessel.result?.registryData?.homePort} />
                <InfoWrapper
                  label={`${t('CreationYear')}:`}
                  value={
                    vessel.result?.registryData?.constructionDate
                      ? new Date(vessel.result?.registryData?.constructionDate).getFullYear()
                      : undefined
                  }
                />
              </div>
              <div className={styles.column}>
                <InfoWrapper label={`${t('RegistryNumber')}:`} value={vessel.result?.registryData?.registryNumber} />
                <InfoWrapper label={`${t('Registry')}:`} value={vessel.result?.registryData?.registry} />
              </div>
            </div>
            <div className={styles.inmarsat_data_block}>
              <InfoWrapper label={`${t('Inmarsat')}:`} value={inmarsat} dots={false} />
            </div>

            <div className={styles.columned_data_block}>
              <div className={styles.column}>
                <InfoWrapper label={`${t('PowerPlantType')}:`} value={vessel.result?.registryData?.powerPlantType} />
                <InfoWrapper label={`${t('MainEngine')}:`} value={vessel.result?.registryData?.mainEngine} />
                <InfoWrapper label={`${t('PropellerInfo')}:`} value={vessel.result?.registryData?.propellerInfo} />
              </div>
              <div className={styles.column}>
                <InfoWrapper label={`${t('FuelType')}:`} value={vessel.result?.registryData?.fuelType} />
                <InfoWrapper label={`${t('FuelBunker')}:`} value={withFuelBunker} />
                <InfoWrapper label={`${t('TheLatestMotionDate')}:`} value={lastMovementDateTime} />
                <InfoWrapper label={`${t('TrackedSince')}:`} value={firstPositionDateTime} />
              </div>
            </div>

            <div className={styles.tables}>
              <Tabs tabs={tabs} value={tabValue} onChange={handleChange} />
              <TabPanel id="vessel-calls" value={tabValue}>
                <div className={styles.ship_calls}>
                  <div className={styles.table_header}>
                    <Typo value={`${t('PortEvents')}:`} size="m" />
                  </div>
                  {vesselEventsRequest.isLoading || (vesselEvents?.results && vesselEvents.results.length > 0 && tableData) ? (
                    <div className={styles.vessel_calls}>
                      {vesselEventsRequest.isLoading && <span className={styles.loading}>{t('SearchingText')}</span>}
                      {vesselEvents?.results && vesselEvents.results.length > 0 && tableData && (
                        <>
                          <div className={styles.table_container}>
                            <Table
                              manualSort={true}
                              sorting={sorting}
                              columns={columns}
                              data={tableData}
                              setSorting={setSorting}
                            />
                          </div>
                          <Pagination
                            totalCount={vesselEvents.results.length}
                            currentPage={currentPage}
                            pageChangeHandler={setCurrentPage}
                            rowsPerPage={20}
                          />
                        </>
                      )}
                    </div>
                  ) : (
                    <p className={styles.no_data}>
                      {!vesselEventsRequest.isLoading && vesselEvents?.results?.length === 0
                        ? `${t('NoResultsFound')}`
                        : t('SearchingText')}
                    </p>
                  )}
                </div>
              </TabPanel>
              <TabPanel id="anchorage" value={tabValue}>
                <div className={styles.ship_calls}>
                  <AnchorageEventsTable mmsi={Number(mmsi)} />
                </div>
              </TabPanel>
              <TabPanel id="similar-vessels" value={tabValue}>
                <div className={styles.ship_calls}>
                  <SimilarShipsTable mmsi={Number(mmsi)} />
                </div>
              </TabPanel>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
